import React, { useState, useEffect } from 'react';

import {
    EyeOutline,
    ColorPaletteOutline,
    BookOutline,
    PodiumOutline,
    PersonOutline,
    PlayOutline,
    ChevronBackOutline,
    ChevronForwardOutline,
} from 'react-ionicons';

import VisionLogo from '../assets/VisionLogo.png';

function goHome() {
    window.location.href = '/home';
}

const NAV_ITEMS = [
    { key: 'vision',            label: 'Vision',            Icon: EyeOutline },
    { key: 'color guesser',     label: 'Color Guesser',     Icon: ColorPaletteOutline },
    { key: 'blindfold puzzles', label: 'Opening Puzzles',   Icon: BookOutline },
    { key: 'leaderboard',       label: 'Leaderboard',       Icon: PodiumOutline },
    { key: 'profile',           label: 'My Profile',        Icon: PersonOutline },
];

const DEMO_ITEM = { key: 'demo', label: 'Demo', Icon: PlayOutline };

export default function SideBar({ activePage, setActivePage, onCollapsedChange }) {
    const [collapsed, setCollapsed] = useState(window.innerWidth < 768);

    // Notify parent whenever collapsed state changes
    useEffect(() => {
        if (onCollapsedChange) {
            onCollapsedChange(collapsed);
        }
    }, [collapsed, onCollapsedChange]);

    // Respond to window resize: auto-collapse on small screens
    useEffect(() => {
        function handleResize() {
            if (window.innerWidth < 768) {
                setCollapsed(true);
            }
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggle = () => setCollapsed(prev => !prev);

    const ICON_COLOR = '#aaa';
    const ICON_SIZE = '22px';

    function handleNav(key) {
        if (key === 'demo') {
            window.location.href = '/demo';
        } else {
            setActivePage(key);
        }
    }

    function NavItem({ item }) {
        const isActive = activePage === item.key;
        const { Icon } = item;
        return (
            <li>
                <button
                    onClick={() => handleNav(item.key)}
                    title={collapsed ? item.label : undefined}
                    className={[
                        'flex items-center gap-3 w-full text-left rounded-lg cursor-pointer',
                        'transition-colors duration-150',
                        collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3',
                        isActive
                            ? 'bg-primary/20 text-primary font-semibold'
                            : 'hover:bg-base-300 text-base-content',
                    ].join(' ')}
                    style={{ border: 'none', background: isActive ? undefined : undefined }}
                >
                    <span className="flex-shrink-0" style={{ lineHeight: 0 }}>
                        <Icon
                            color={isActive ? '#ff7900' : ICON_COLOR}
                            height={ICON_SIZE}
                            width={ICON_SIZE}
                        />
                    </span>
                    {!collapsed && (
                        <span className={`text-sm whitespace-nowrap overflow-hidden transition-all duration-300 ${isActive ? 'text-primary' : ''}`}>
                            {item.label}
                        </span>
                    )}
                </button>
            </li>
        );
    }

    return (
        <div
            className="flex flex-col bg-base-200 h-screen transition-all duration-300 ease-in-out overflow-hidden"
            style={{ width: collapsed ? 64 : 230, minWidth: collapsed ? 64 : 230 }}
        >
            {/* Logo area + toggle in one row */}
            <div className={`flex items-center py-4 transition-all duration-300 ease-in-out ${collapsed ? 'justify-center px-2 gap-0' : 'px-3 gap-2'}`}>
                <div className="flex items-center gap-2 flex-1 cursor-pointer overflow-hidden" onClick={goHome}>
                    <img
                        src={VisionLogo}
                        alt="SixtyFour"
                        style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }}
                    />
                    {!collapsed && (
                        <span className="text-sm font-bold text-base-content whitespace-nowrap overflow-hidden">
                            SixtyFour
                        </span>
                    )}
                </div>
                <button
                    onClick={toggle}
                    className="btn btn-ghost btn-xs p-1 rounded-lg hover:bg-base-300 flex-shrink-0"
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    style={{ border: 'none' }}
                >
                    {collapsed
                        ? <ChevronForwardOutline color={ICON_COLOR} height="16px" width="16px" />
                        : <ChevronBackOutline color={ICON_COLOR} height="16px" width="16px" />
                    }
                </button>
            </div>

            <div className="divider my-0 px-3" />

            {/* Main nav */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2">
                <ul className="flex flex-col gap-1">
                    {NAV_ITEMS.map(item => (
                        <NavItem key={item.key} item={item} />
                    ))}
                </ul>

                <div className="divider my-2 px-2" />

                <ul className="flex flex-col gap-1">
                    <NavItem item={DEMO_ITEM} />
                </ul>
            </nav>
        </div>
    );
}
